/**
 * Selected product entity
 *
 * Wraps up the logic for selecting a product as well as calculating price etc.
 *
 * @author John Noel <john.noel@joipolloi.com>
 * @package Minifigs
 */

import { getMoneyComponents, getShopCurrencyCode } from '../utils/currency';

class SelectedProduct {
    constructor(
        product,
        category,
        attributes = [],
        addons = [],
        product_attributes,
        attributeTerms,
        productFormJsonValues
    ) {
        this.product = product;
        this.category = category;
        this.attributes = attributes;
        this.addons = addons;
        this.productMatchAttr = product_attributes;
        this.attributeTerms = attributeTerms;
        this.productFormJsonValues = productFormJsonValues;
    }

    getPrice() {
        let price = 0;
        let sourceCurrencyCode = getShopCurrencyCode();

        if (this.category?.id === 'heads' && this.productMatchAttr?.price) {
            price = parseFloat(this.productMatchAttr?.price.amount);
            sourceCurrencyCode = this.productMatchAttr.price.currencyCode || sourceCurrencyCode;
        } else if (this.attributes?.price) {
            price = parseFloat(this.attributes?.price.amount);
            sourceCurrencyCode = this.attributes.price.currencyCode || sourceCurrencyCode;
        } else {
            if (this.product.variants.edges) {
                price = parseFloat(this.product.variants?.edges[0]?.node.price?.amount);
                sourceCurrencyCode = this.product.variants?.edges[0]?.node.price?.currencyCode || sourceCurrencyCode;
            } else {
                price = parseFloat(this.product.variants.nodes && this.product.variants?.nodes[0]?.price?.amount);
                sourceCurrencyCode = this.product.variants?.nodes[0]?.price?.currencyCode || sourceCurrencyCode;
            }
        }

        const attr = this.attributes && this.attributes.length !== 0 ? this.attributes : this.product;
        const searchValue = attr?.id;

        if (attr && attr.length !== 0 && this.product.variants) {
            for (let i = 0; i < this.product.variants.length; i += 1) {
                const v = this.product.variants[i];
                if (v.id === searchValue) {
                    price = parseFloat(v.compare_at_price ? v.compare_at_price : v.price);
                    break;
                }
            }
        }

        const components = [{ amount: price, currencyCode: sourceCurrencyCode }];

        let propCost = this.productFormJsonValues?.properties?._boldVariantPrices;
        if (propCost && propCost !== null && propCost !== 'undefined') {
            propCost = propCost.split(',');
            propCost.forEach((cost) => {
                const costFloat = parseFloat(cost);
                if (Number.isFinite(costFloat)) {
                    components.push({ amount: costFloat / 100, currencyCode: getShopCurrencyCode() });
                }
            });
        }
        return { components };
    }

    /**
     * This will get the image assigned to the particular variation
     * Mainly used in the overlay type of variable products
     *
     * @return {*} - string or none
     */
    getOverLayImage() {
        let image = (this.product.images && this.product.images[0] && this.product.images[0].src) || null;
        const attr = this.attributes;
        const searchValue = attr?.id;

        if (!this.product.images) {
            console.error('No images found');
            return image;
        }

        for (let i = 0; i < this.product.images.length; i += 1) {
            const v = this.product.images[i];
            if (v) {
                if (v.variant_ids[0]) {
                    if (searchValue === v.variant_ids[0] && v.variant_ids[0] !== undefined) {
                        image = v.src;
                    }
                }
            }
        }
        // this displays the main image(s) on the left of the select dropdown
        return image;
    }

    /**
     * Calculate the cost of the selected products
     *
     * @param {object} selected
     * @return {object} Source-currency price components, converted only for display.
     */
    static calculateCost(products) {
        const components = [];
        const processItem = (item) => {
            if (!item) return;
            let obj = item;
            if (typeof obj.getPrice !== 'function') {
                obj = new SelectedProduct(
                    obj.product,
                    obj.category,
                    obj.attributes,
                    obj.addons,
                    obj.productMatchAttr,
                    obj.attributeTerms,
                    obj.productFormJsonValues
                );
            }
            components.push(...getMoneyComponents(obj.getPrice()));
        };

        for (let k in products) {
            if (k === 'accessories' && Array.isArray(products[k])) {
                products[k].forEach((acc) => processItem(acc));
            } else {
                processItem(products[k]);
            }
        }

        return { components };
    }

    toJSON() {
        // Ensure productFormJsonValues carries an id (variant id) for cart operations
        if (this.productFormJsonValues) {
            const pfjv = this.productFormJsonValues;
            const normalise = (raw) => (typeof raw === 'string' ? raw : raw);
            const currentVariantId = this.productMatchAttr?.id || this.attributes?.id || this.attributes?.node?.id;
            // If id missing OR stale (doesn't match current variant), overwrite.
            if (!pfjv.id || (currentVariantId && normalise(pfjv.id) !== normalise(currentVariantId))) {
                if (currentVariantId) {
                    pfjv.id = currentVariantId;
                } else if (this.product && this.product.variants && this.product.variants.edges) {
                    pfjv.id = this.product.variants.edges[0]?.node?.id;
                } else if (this.product && this.product.variants && this.product.variants.nodes) {
                    pfjv.id = this.product.variants.nodes[0]?.id;
                }
            }
            if (!pfjv.quantity) {
                pfjv.quantity = 1;
            }
        }
        return {
            product: this.product,
            category: this.category,
            attributes: this.attributes,
            addons: this.addons,
            productMatchAttr: this.productMatchAttr,
            attributeTerms: this.attributeTerms,
            productFormJsonValues: this.productFormJsonValues,
        };
    }
}

export default SelectedProduct;
